import dynamic from 'next/dynamic';
import { useRouter } from 'next/router';

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDidMount } from 'rooks';
import shallow from 'zustand/shallow';

import { TranscriptQuery } from 'api';
import { client } from 'f';
import { trackVWOCustomConversionById } from 'lib/analytics/vwo';
import { useAuthenticated } from 'lib/auth';
import { useDidMount as useMounted } from 'lib/hooks/useDidMount';
import { useIsBreakpointWidth } from 'lib/hooks/useIsBreakpointWidth';
import { VideoData } from 'lib/pages/talks/slug/[...slug].props';

import ContentContainer from 'components/content-container';
import TranslationBanner from 'components/pages/dubbing/slug/TranslationBanner';
import Hero from 'components/pages/dubbing/slug/hero';
import useVideoPlayerStore from 'components/video-player-dubbing/store';

import { breakpoints } from '../../../../../design-tokens/constants';

import { useSailthruTrack } from 'components/pages/talks/slug/hooks';
import { delay, isEmpty, isNil } from 'lodash-es';
import { useDubSurvey } from './Summary/store';
import acceptedTalks from './accepted-talks.json';
import { useTalkPaddingOnResize } from './hooks';
import { useTalkPageContext } from './talk-page-context';
import { sanitizePathname } from './utils';

const TalkHead = dynamic(() => import('./TalkHead'));

/**
 * Using ssr: false for TalkContent is necessary because:
 * 1. It depends on client-side authentication state with useAuthenticated() hook
 * 2. It utilizes useNavUserInformationQuery with ssr: false explicitly set
 * 3. It contains dynamic UI components that rely on client-side state (isTalkDetailsRollupVisible)
 * 4. It renders the Comments component which integrates with OpenWeb requiring client-side DOM
 */
const TalkContent = dynamic(() => import('./TalkPage/Content'), {
  ssr: false
});

/**
 * Using ssr: false for AsideSections is necessary because:
 * 1. It uses DOM mutation observers with useMutationObserverRef for detecting changes
 * 2. It performs layout measurements with getBoundingClientRect for sidebar dimensions
 * 3. It has conditional visibility based on viewport size with useIsBreakpointWidth
 * 4. It provides a skeleton loader for better perceived performance on client
 */
const AsideSections = dynamic(() => import('./AsideSection'), {
  ssr: false
});

/**
 * Using ssr: false for SignInModal is necessary because:
 * 1. It contains authentication links that need access to window.location
 * 2. It's only shown conditionally when user interactions trigger it
 * 3. It uses the Modal component which manages focus and other DOM-specific behaviors
 * 4. It's not needed for initial page rendering and improves page load performance
 */
const SignInModal = dynamic(
  () => import('./Modal').then(mod => mod.SignInModal),
  {
    ssr: false
  }
);

/**
 * Using ssr: false for TalkDetails is necessary because:
 * 1. It makes API requests using useTalkDetailsLazyQuery with client-side fetch policy
 * 2. It depends on videoPlayerStore state which is initialized client-side
 * 3. It renders complex UI that requires ref usage for scroll positioning (actionsRef)
 * 4. It conditionally renders components based on transcript visibility state
 */
const TalkDetails = dynamic(() => import('./TalkDetails'), {
  ssr: false
});

interface TalkPageProps {
  videoData: VideoData;
  transcript: TranscriptQuery;
  commentsEnabled: boolean;
  commentsLoggedInOnly: boolean;
}

const calculatePlayThruPercent = ({
  time,
  duration
}: {
  time: number;
  duration: number;
}) => {
  if (duration < 1) {
    return 0;
  }
  return parseFloat((time / duration).toFixed(2)) * 100;
};

const TalkPage = ({
  videoData,
  transcript,
  commentsEnabled,
  commentsLoggedInOnly
}: TalkPageProps) => {
  const { openModalSummary, closeAllModal, setSurveyResponse } = useDubSurvey();
  const { sendPageview } = useSailthruTrack(videoData);
  const hasMounted = useMounted();
  const isLoggedIn = useAuthenticated();

  const router = useRouter();

  const { language } = useTalkPageContext();

  const currentPlayThruPercent = useVideoPlayerStore(calculatePlayThruPercent);
  const {
    roundedTime,
    isTranscriptVisible,
    onTranscriptToggle,
    onCloseTranscript,
    callPause,
    dubbingLang,
    transcriptLanguage
  } = useVideoPlayerStore(
    state => ({
      roundedTime: state.roundedTime,
      currentTime: state.currentTime,
      isTranscriptVisible: state.isTranscriptVisible,
      onTranscriptToggle: state.onTranscriptToggle,
      onCloseTranscript: state.onCloseTranscript,
      callPause: state.callPause,
      dubbingLang: state.dubbingLang,
      transcriptLanguage: state.transcriptLanguage
    }),
    shallow
  );

  useEffect(() => {
    if (isNil(router.query.audio)) {
      router.replace(
        {
          pathname: router.pathname,
          query: {
            ...router.query,
            audio: dubbingLang
          }
        },
        undefined,
        { shallow: true }
      );
    }

    if (isNil(router.query.language)) {
      router.replace(
        {
          pathname: router.pathname,
          query: {
            ...router.query,
            language: isEmpty(transcriptLanguage) ? 'en' : transcriptLanguage
          }
        },
        undefined,
        { shallow: true }
      );
    }
  }, [router.query]);

  const { videoRef, detailsRef, actionButtonsVisibilityPadding } =
    useTalkPaddingOnResize();

  const sidebarInnerContent = useRef<HTMLDivElement>(null);

  const [isInitialPageLoad, setIsInitialPageLoad] = useState(true);
  const [hasSidebarScrollbar, setHasSidebarScrollbar] = useState(false);
  const [isSignInModalOpen, setIsSignInModalOpen] = useState(false);
  const [playThruBannerState, setPlayThruBannerState] = useState('neverOpened');
  const [sidebarInnerContentHeight, setSidebarInnerContentHeight] = useState(0);

  const [isBodyScrollable, setIsBodyScrollable] = useState(true);

  const isMobileWidth = useIsBreakpointWidth({
    size: 'md',
    breakPointType: 'tui'
  });

  // Set state for sidebars inner content height and whether the sidebar should render a scrollbar
  const renderSidebarScrollbar = () => {
    const sidebarInnerContentEl = sidebarInnerContent.current;
    if (
      sidebarInnerContentEl &&
      typeof sidebarInnerContentEl.getBoundingClientRect === 'function'
    ) {
      const sidebarInnerContentHeight =
        sidebarInnerContentEl.getBoundingClientRect().height;
      setSidebarInnerContentHeight(sidebarInnerContentHeight);
      setHasSidebarScrollbar(sidebarInnerContentHeight > window.innerHeight);
    }
  };

  // Call Sailthru track pageview on video ID change
  useEffect(() => {
    if (!hasMounted || !videoData?.id) return;

    const sendPageviewSailthru = async () => {
      try {
        await sendPageview();
      } catch (error) {
        console.error('Failed to send Sailthru pageview:', error);
      }
    };

    sendPageviewSailthru();
  }, [hasMounted, videoData?.id]);

  useEffect(() => {
    if (currentPlayThruPercent >= 90 && playThruBannerState !== 'closed') {
      setPlayThruBannerState('open');
    }
  }, [currentPlayThruPercent]);

  useEffect(() => {
    if (window.innerWidth <= breakpoints.lg) {
      setIsBodyScrollable(!isTranscriptVisible);
    }

    if (!isInitialPageLoad) {
      const url = new URL(router.asPath, 'https://www.ted.com');

      if (isTranscriptVisible) {
        url.pathname += '/transcript';
      } else {
        url.pathname = url.pathname.replace('/transcript', '');
      }

      const sanitizedPathname = sanitizePathname(url);

      router.replace(
        {
          pathname: '/dubbing/[...slug]',
          query: {
            ...router.query
          }
        },
        sanitizedPathname,
        { shallow: true }
      );
    }
  }, [isTranscriptVisible]);

  useEffect(() => {
    /* Will add overflow hidden to body so there isn't a double scrollbar
    or the ability to scroll the document while transcript panel is open
    */
    const body = document.querySelector('body');
    if (!body) return;

    if (!isBodyScrollable) {
      body.style.overflow = 'hidden';
    } else {
      body.style.removeProperty('overflow');
    }
  }, [isBodyScrollable]);

  useEffect(() => {
    renderSidebarScrollbar();
  }, [sidebarInnerContentHeight]);

  useEffect(() => {
    if (isMobileWidth && isTranscriptVisible) {
      setIsBodyScrollable(false);
    } else {
      setIsBodyScrollable(true);
    }
  }, [isMobileWidth, isTranscriptVisible]);

  // Full page over take to display transcript component for mobile devices
  const toggleTranscriptPanel = useCallback(() => {
    if (isTranscriptVisible) {
      onCloseTranscript();
    }
    onTranscriptToggle(!isTranscriptVisible);
  }, [isTranscriptVisible, onCloseTranscript, onTranscriptToggle]);

  useDidMount(() => {
    setIsInitialPageLoad(false);
  });

  useEffect(() => {
    if (roundedTime === 15) {
      trackVWOCustomConversionById(210);
    }
  }, [roundedTime]);

  const renderAsideSections = () => (
    <AsideSections
      ref={sidebarInnerContent}
      renderSidebarScrollbar={renderSidebarScrollbar}
      toggleTranscriptPanel={toggleTranscriptPanel}
      hasSidebarScrollbar={hasSidebarScrollbar}
      isTranscriptVisible={isTranscriptVisible || false}
      hasRelatedVideos={videoData.relatedVideos.length > 0}
      hasTranslations={videoData.hasTranslations}
      hasVideoTopics={videoData.topics.nodes.length > 0}
      videoId={videoData.id}
      videoTopics={videoData.topics.nodes}
    />
  );

  useEffect(() => {
    if (
      videoData.duration - 1 === roundedTime &&
      hasMounted &&
      dubbingLang !== 'en'
    ) {
      setSurveyResponse(null);
      callPause(true);
      onTranscriptToggle(false);
      delay(() => openModalSummary(), 1000);
    }
  }, [
    roundedTime,
    hasMounted,
    openModalSummary,
    callPause,
    videoData.duration
  ]);

  useEffect(() => {
    if (hasMounted) {
      closeAllModal();
    }

    return () => {
      closeAllModal();
    };
  }, [closeAllModal, router.asPath, hasMounted]);

  useEffect(() => {
    closeAllModal();

    return () => {
      closeAllModal();
    };
  }, []);

  const internalLanguageCode = useMemo(() => {
    const langArray = acceptedTalks.find(talk => talk.id === videoData.id)
      ?.playerData.languages;

    return langArray?.find(lang => lang.languageCode === router.query.language)
      ?.languageCode;
  }, [router.query.language, videoData.id]);

  return (
    <>
      <TranslationBanner internalLanguageCode={internalLanguageCode ?? 'en'} />

      <SignInModal
        isOpen={isSignInModalOpen}
        onDismiss={() => setIsSignInModalOpen(false)}
        showButtons={client && hasMounted}
      />

      <TalkHead video={videoData} language={language} transcript={transcript} />
      <ContentContainer className="bg-white">
        <div
          className="flex w-full flex-col lg:flex-row"
          style={{
            flex: 1,
            paddingLeft: `${actionButtonsVisibilityPadding.left}px`,
            paddingRight: `${actionButtonsVisibilityPadding.right}px`
          }}
        >
          <div className="flex w-full flex-col pt-4">
            <Hero ref={videoRef} video={videoData} />

            <TalkDetails
              actionsRef={detailsRef}
              displayCommentsButton={false}
              video={{ language, ...videoData }}
            />

            <TalkContent
              {...{ videoData, displayComments: false, setIsSignInModalOpen }}
            />
            {isMobileWidth && renderAsideSections()}
          </div>

          {!isMobileWidth && renderAsideSections()}
        </div>
      </ContentContainer>
    </>
  );
};

export default TalkPage;
